/* Dispatch 13 -- InstallAppButton.
 *
 * Renders a "Install app" chip in the mobile top bar when:
 *   1) The `beforeinstallprompt` event has fired (Android Chrome / Edge /
 *      desktop Chrome), OR
 *   2) The browser is iOS Safari and the app is not yet standalone
 *      (fallback shows an "Install app" chip that opens an instruction
 *      sheet: Share -> Add to Home Screen).
 *
 * Hidden entirely when the app is already running standalone
 * (`useIsStandalone() === true`).
 *
 * Available to every authenticated user regardless of role -- PWA install
 * is a UX benefit, not an admin action.
 */
import React, { useEffect, useState } from "react";
import { Download, Share as ShareIcon, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useIsStandalone } from "@/lib/viewport";
import { isIos, isSafari } from "@/lib/iosPwa";

const DISMISSED_KEY = "sharda_install_btn_dismissed_at";

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts || Number.isNaN(ts)) return false;
    // Dismissal lasts 24 hours -- short enough that users get another shot
    // after a full day, long enough not to nag.
    return Date.now() - ts < 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function InstallAppButton({ variant = "chip" }) {
  const standalone = useIsStandalone();
  const [evt, setEvt] = useState(null);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const [_forceShow, _setForceShow] = useState(0);

  useEffect(() => {
    if (standalone) return undefined;
    const handler = (e) => {
      // Capture the event and stop the browser from firing its own mini-
      // infobar; we surface the CTA in-app instead.
      e.preventDefault();
      setEvt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [standalone]);

  // Already standalone -> never render.
  if (standalone) return null;
  // Dismissed within the last 24 hours -> hidden (users can still trigger
  // install from the Home push nudge or manual browser controls).
  if (wasRecentlyDismissed()) return null;

  const iosCandidate = isIos() && isSafari();
  const androidCandidate = !!evt;

  // Neither prompt available AND not iOS Safari -> nothing sensible to
  // show. (Desktop Firefox, Brave without PWA support, etc.)
  if (!iosCandidate && !androidCandidate) return null;

  const onClick = async () => {
    if (androidCandidate) {
      try {
        evt.prompt();
        const result = await evt.userChoice;
        if (result.outcome === "accepted") {
          toast.success("App installed");
        } else {
          try {
            localStorage.setItem(DISMISSED_KEY, String(Date.now()));
          } catch {
            /* localStorage unavailable -- silent */
          }
        }
        setEvt(null);
      } catch (err) {
        toast.error("Install failed");
      }
    } else if (iosCandidate) {
      setIosSheetOpen(true);
    }
  };

  const chipClass =
    variant === "topbar"
      ? "inline-flex items-center gap-1 h-8 px-2 rounded-lg bg-gradient-primary text-white text-[11px] font-medium hover:opacity-90 active:scale-95 transition"
      : "inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-gradient-primary text-white text-xs font-medium hover:opacity-90 active:scale-95 transition";

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid="install-app-btn"
        className={chipClass}
        aria-label="Install app"
      >
        <Download className="h-3.5 w-3.5" />
        <span>Install</span>
      </button>
      <Dialog open={iosSheetOpen} onOpenChange={setIosSheetOpen}>
        <DialogContent data-testid="install-app-ios-sheet" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-widest">INSTALL SHARDA ENERGY OS</DialogTitle>
            <DialogDescription>
              Add to Home Screen for a full-screen app experience.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-brand-blue/20 text-sky text-xs font-bold">1</span>
              <span className="flex-1">
                Tap the <ShareIcon className="inline h-4 w-4 align-middle" /> <b>Share</b> icon
                <span className="text-foreground/60"> at the bottom of Safari.</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-brand-blue/20 text-sky text-xs font-bold">2</span>
              <span className="flex-1">
                Scroll down and choose <Plus className="inline h-4 w-4 align-middle" /> <b>Add to Home Screen</b>.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-brand-blue/20 text-sky text-xs font-bold">3</span>
              <span className="flex-1">
                Tap <b>Add</b>. Sharda Energy OS appears on your home screen like a native app.
              </span>
            </li>
          </ol>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => {
                try {
                  localStorage.setItem(DISMISSED_KEY, String(Date.now()));
                } catch {
                  /* localStorage unavailable -- silent */
                }
                setIosSheetOpen(false);
              }}
              data-testid="install-app-ios-dismiss"
              className="text-xs text-foreground/70 hover:text-foreground px-3 py-2"
            >
              Maybe later
            </button>
            <button
              onClick={() => setIosSheetOpen(false)}
              data-testid="install-app-ios-close"
              className="text-xs bg-gradient-primary text-white px-4 py-2 rounded-lg"
            >
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default InstallAppButton;
